/**
 * Employer Portal — Sortable Table View
 * Mobile: stacked cards. Desktop: dense sortable table.
 */
(function () {
  'use strict';

  var config = window.PORTAL_CONFIG || {};
  var base = config.gatewayUrl || '/apps/portal';
  var email = config.email || '';
  var purchases = [];
  var sortCol = 'purchased_at';
  var sortDir = -1; // -1 = desc
  var filterStatus = 'all';
  var isArchive = !!(config.archiveMode);
  var filterHire = isArchive ? 'no' : '__no_no'; // archive: show only Passed; normal: hide Passed
  var filterText = '';
  var timeFilter = 'all';
  var currentPage = 1;
  var PAGE_SIZE = 25;
  var filteredPurchases = [];

  if (!email) return;

  function getAuthHeaders() {
    var h = { 'Content-Type': 'application/json' };
    if (config.authToken) { h['X-Auth-Token'] = config.authToken; }
    if (config.customerId) { h['X-Customer-Id'] = String(config.customerId); }
    return h;
  }

  // ── API ──────────────────────────────────────────────────

  function fetchPurchases(cb) {
    fetch(base + '/purchases?email=' + encodeURIComponent(email), { headers: getAuthHeaders() })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        purchases = data.purchases || [];
        purchases.forEach(function (p) {
          p.outreach_status = deriveStatus(p);
          p.is_unread = !p.first_viewed_at;
        });
        cb();
      })
      .catch(function (e) {
        console.error('Portal fetch error:', e);
        var el = document.getElementById('portal-loading');
        if (el) el.innerHTML = '<p class="sh-alert sh-alert--error">Unable to load candidates. Please refresh the page.</p>';
      });
  }

  function updateTracking(purchaseId, data, cb) {
    data.purchase_id = purchaseId;
    data.customer_email = email;
    fetch(base + '/tracking', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    })
      .then(function (r) { return r.json(); })
      .then(cb || function () {})
      .catch(function (e) {
        console.error('Portal update error:', e);
        shToast('Save failed', 'error');
      });
  }

  // ── Sort ─────────────────────────────────────────────────

  var hireOrder = { '': 0, maybe: 1, yes: 2, no: 3 };
  var statusOrder = { 'new': 0, contacted: 1, phone: 2, in_person: 3, hired: 4 };

  function deriveStatus(p) {
    if (p.in_person_interview) return 'in_person';
    if (p.phone_interview) return 'phone';
    if (p.contacted) return 'contacted';
    return 'new';
  }

  function statusToBools(status) {
    return {
      contacted: status !== 'new' ? 1 : 0,
      phone_interview: (status === 'phone' || status === 'in_person' || status === 'hired') ? 1 : 0,
      in_person_interview: (status === 'in_person' || status === 'hired') ? 1 : 0
    };
  }

  function cmpValues(va, vb, col, dir) {
    if (va == null) va = '';
    if (vb == null) vb = '';
    if (col === 'hire_status') {
      return ((hireOrder[va] || 0) - (hireOrder[vb] || 0)) * dir;
    }
    if (col === 'outreach_status') {
      return ((statusOrder[va] || 0) - (statusOrder[vb] || 0)) * dir;
    }
    if (typeof va === 'number') return (va - vb) * dir;
    if (typeof va === 'boolean' || va === 0 || va === 1) return ((va ? 1 : 0) - (vb ? 1 : 0)) * dir;
    return String(va).localeCompare(String(vb)) * dir;
  }

  function sortPurchases() {
    purchases.sort(function (a, b) {
      return cmpValues(a[sortCol], b[sortCol], sortCol, sortDir);
    });
  }

  // ── Time filter for summary metrics ─────────────────────

  function filterByTime(list) {
    if (timeFilter === 'all') return list;
    var now = Date.now();
    var cutoff;
    if (timeFilter === 'week') cutoff = now - 7 * 86400000;
    else if (timeFilter === 'month') cutoff = now - 30 * 86400000;
    else if (timeFilter === 'quarter') cutoff = now - 90 * 86400000;
    else return list;
    return list.filter(function (p) {
      return p.purchased_at && new Date(p.purchased_at).getTime() >= cutoff;
    });
  }

  // ── Filter & Pagination ─────────────────────────────────

  function applyFilters() {
    var q = filterText.toLowerCase();
    filteredPurchases = purchases.filter(function (p) {
      if (filterStatus !== 'all') {
        if (filterStatus === 'interviewed') {
          if (p.outreach_status !== 'phone' && p.outreach_status !== 'in_person') return false;
        } else if (p.outreach_status !== filterStatus) {
          return false;
        }
      }
      if (filterHire === '__no_no') {
        if (p.hire_status === 'no') return false;
      } else if (filterHire !== 'all') {
        var hv = p.hire_status || '';
        if (hv === 'pending') hv = '';
        if (hv !== filterHire) return false;
      }
      if (q) {
        var name = (p.candidate_name || '').toLowerCase();
        var metro = (p.candidate_metro || '').toLowerCase();
        if (name.indexOf(q) === -1 && metro.indexOf(q) === -1) return false;
      }
      return true;
    });
  }

  function paginateSlice() {
    var start = (currentPage - 1) * PAGE_SIZE;
    return filteredPurchases.slice(start, start + PAGE_SIZE);
  }

  function totalPages() {
    return Math.max(1, Math.ceil(filteredPurchases.length / PAGE_SIZE));
  }

  function setSort(col) {
    if (sortCol === col) {
      sortDir = sortDir * -1;
    } else {
      sortCol = col;
      sortDir = col === 'purchased_at' ? -1 : 1;
    }
    render();
  }

  // ── Render ───────────────────────────────────────────────

  var columns = [
    { key: 'candidate_name', label: 'Name', type: 'name_link' },
    { key: 'purchased_at', label: 'Date', type: 'date' },
    { key: 'candidate_metro', label: 'Area' },
    { key: 'pay_range', label: 'Pay Range' },
    { key: 'candidate_phone', label: 'Phone' },
    { key: 'candidate_email', label: 'Email' },
    { key: 'outreach_status', label: 'Status', type: 'status' },
    { key: 'hire_status', label: 'Hire', type: 'hire' },
    { key: 'employer_notes', label: 'Notes', type: 'notes' },
  ];

  // ── Column Toggle Bar ────────────────────────────────────

  var toggleBarInit = false;
  var colVisible = {};
  var COLUMN_KEY = 'skimhire_employer_columns';

  function loadColumnPrefs() {
    try {
      var raw = localStorage.getItem(COLUMN_KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      if (!s || s.v !== 1 || !s.cols) return null;
      return s.cols;
    } catch (e) { return null; }
  }

  function saveColumnPrefs() {
    try {
      var byKey = {};
      columns.forEach(function (col, i) {
        if (col.key === 'candidate_name') return;
        byKey[col.key] = colVisible[i + 1] !== false;
      });
      localStorage.setItem(COLUMN_KEY, JSON.stringify({ v: 1, cols: byKey }));
    } catch (e) { /* localStorage disabled — silently no-op */ }
  }

  function initToggleBar() {
    if (toggleBarInit) return;
    toggleBarInit = true;

    var bar = document.getElementById('col-toggles');
    if (!bar) return;

    var w = window.innerWidth;
    var saved = loadColumnPrefs();

    // "Columns" label (matches Status/Hire/Period label pattern)
    var lbl = document.createElement('span');
    lbl.className = 'filter-label';
    lbl.textContent = 'Columns';
    bar.appendChild(lbl);

    var pillWrap = document.createElement('div');
    pillWrap.className = 'filter-pills';

    columns.forEach(function (col, i) {
      var n = i + 1;
      var isName = col.key === 'candidate_name';

      // Name column is always visible, no toggle needed
      if (isName) { colVisible[n] = true; return; }

      // Responsive defaults based on viewport width
      var visible = true;
      if (w < 1100 && col.key === 'candidate_metro') visible = false;
      if (w < 860 && (col.key === 'candidate_email' || col.key === 'pay_range')) visible = false;
      if (w < 680 && col.key === 'employer_notes') visible = false;

      // Saved preference (keyed by col.key so column reorders don't corrupt state)
      // overrides the viewport default.
      if (saved && typeof saved[col.key] === 'boolean') visible = saved[col.key];

      colVisible[n] = visible;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'filter-pill' + (visible ? ' active' : '');
      btn.textContent = col.label;
      btn.setAttribute('data-col', String(n));
      btn.addEventListener('click', function () {
        colVisible[n] = !colVisible[n];
        btn.classList.toggle('active', colVisible[n]);
        applyColumnVisibility();
        saveColumnPrefs();
      });
      pillWrap.appendChild(btn);
    });

    bar.appendChild(pillWrap);
    bar.style.display = '';
  }

  function applyColumnVisibility() {
    var list = document.getElementById('candidates-list');
    if (!list) return;
    for (var n = 2; n <= columns.length; n++) {
      if (colVisible[n] === false) {
        list.classList.add('hide-col-' + n);
      } else {
        list.classList.remove('hide-col-' + n);
      }
    }
  }

  function render() {
    var loading = document.getElementById('portal-loading');
    var empty = document.getElementById('portal-empty');
    var list = document.getElementById('candidates-list');
    var summary = document.getElementById('portal-summary');

    if (loading) loading.style.display = 'none';

    if (purchases.length === 0) {
      if (empty) empty.style.display = 'block';
      if (list) list.style.display = 'none';
      if (summary) summary.style.display = 'none';
      return;
    }

    // Summary (filtered by time period for metrics)
    var timePurchases = filterByTime(purchases);
    if (isArchive) {
      timePurchases = timePurchases.filter(function (p) { return p.hire_status === 'no'; });
    }
    var nContacted = 0, nInterviewed = 0, nHired = 0;
    timePurchases.forEach(function (p) {
      if (p.contacted) nContacted++;
      if (p.phone_interview || p.in_person_interview) nInterviewed++;
      if (p.hire_status === 'yes') nHired++;
    });
    if (summary) {
      summary.style.display = 'block';
      setText('total-purchased', timePurchases.length);
      setText('total-contacted', nContacted);
      setText('total-interviewed', nInterviewed);
      setText('total-hired', nHired);
    }

    // Filter -> Sort -> Paginate
    applyFilters();
    filteredPurchases.sort(function (a, b) {
      return cmpValues(a[sortCol], b[sortCol], sortCol, sortDir);
    });
    if (currentPage > totalPages()) currentPage = totalPages();

    var isMobile = window.innerWidth <= 480;
    var pageRows = isMobile ? filteredPurchases : paginateSlice();

    var html = buildFilterBarHTML();
    if (filteredPurchases.length === 0) {
      html += '<div class="portal-no-results">No candidates match your filters. <a href="#" id="clear-filters">Clear filters</a></div>';
    } else {
      html += buildTable(pageRows, 'main');
      if (!isMobile) html += buildPaginationHTML();
    }

    if (list) {
      list.innerHTML = html;
      list.style.display = 'block';
      var exportBtn = document.createElement('button');
      exportBtn.textContent = 'Export CSV';
      exportBtn.className = 'portal-export-btn';
      exportBtn.addEventListener('click', exportCSV);
      list.insertBefore(exportBtn, list.firstChild);
      attachHandlers();
      attachFilterHandlers();
      initToggleBar();
      applyColumnVisibility();
      highlightActiveSummaryCard();
    }

    // Archive link (only on normal portal, not archive page)
    var archiveLink = document.getElementById('portal-archive-link');
    if (archiveLink && !isArchive) {
      var archivedCount = purchases.filter(function (p) { return p.hire_status === 'no'; }).length;
      if (archivedCount > 0) {
        archiveLink.style.display = 'block';
        archiveLink.innerHTML = '<a href="/pages/employer-archive">View ' + archivedCount + ' Archived Candidate' + (archivedCount !== 1 ? 's' : '') + ' \u2192</a>';
      } else {
        archiveLink.style.display = 'none';
      }
    }
  }

  function buildTable(rows, id) {
    var h = '<div class="table-wrap"><table class="portal-table" data-table="' + id + '"><thead><tr>';
    columns.forEach(function (c) {
      var cls = '';
      if (sortCol === c.key) cls = sortDir === 1 ? 'sorted-asc' : 'sorted-desc';
      h += '<th class="' + cls + '" data-sort="' + c.key + '">'
        + c.label + '<span class="sort-arrow"></span></th>';
    });
    h += '</tr></thead><tbody>';
    rows.forEach(function (p) {
      var rowClasses = [];
      if (p.hire_status === 'no') rowClasses.push('row-passed');
      if (p.is_unread) rowClasses.push('row-unread');
      var rowCls = rowClasses.length ? ' class="' + rowClasses.join(' ') + '"' : '';
      h += '<tr data-id="' + esc(p.id) + '"' + rowCls + '>';
      columns.forEach(function (c) { h += renderCell(p, c); });
      h += '</tr>';
    });
    h += '</tbody></table></div>';
    return h;
  }

  // ── Filter Bar HTML ─────────────────────────────────────

  function buildFilterBarHTML() {
    var h = '<div class="portal-filters">';
    // Status pills
    h += '<div class="filter-row">';
    h += '<span class="filter-label">Status</span>';
    h += '<div class="filter-pills" data-filter="status">';
    [['all','All'],['new','New'],['contacted','Contacted'],['phone','Phone'],['in_person','In-Person'],['hired','Hired']].forEach(function (s) {
      var active = filterStatus === s[0] ? ' active' : '';
      h += '<button class="filter-pill' + active + '" data-status="' + s[0] + '">' + s[1] + '</button>';
    });
    h += '</div></div>';
    // Hire pills (replaced with note in archive mode)
    if (isArchive) {
      h += '<div class="filter-row">';
      h += '<span class="filter-label">Hire</span>';
      h += '<span class="filter-archive-note">Showing candidates marked <strong>No</strong></span>';
      h += '</div>';
    } else {
      h += '<div class="filter-row">';
      h += '<span class="filter-label">Hire</span>';
      h += '<div class="filter-pills" data-filter="hire">';
      [['__no_no','Active'],['all','All'],['yes','Yes'],['maybe','Maybe'],['no','Passed'],['','Undecided']].forEach(function (s) {
        var active = filterHire === s[0] ? ' active' : '';
        h += '<button class="filter-pill' + active + '" data-hire="' + s[0] + '">' + s[1] + '</button>';
      });
      h += '</div></div>';
    }
    // Search + count
    h += '<div class="filter-row">';
    h += '<input type="text" id="portal-search" class="portal-search-input" placeholder="Search name or area\u2026" value="' + esc(filterText) + '">';
    h += '<span class="filter-result-count">' + filteredPurchases.length + ' of ' + purchases.length + ' candidates</span>';
    h += '</div>';
    // Mobile sort (visible only at <=768px where thead is hidden)
    var sortOptions = [
      ['purchased_at', 'Date'],
      ['candidate_name', 'Name'],
      ['outreach_status', 'Status'],
      ['hire_status', 'Hire'],
      ['candidate_metro', 'Area']
    ];
    h += '<div class="portal-sort-mobile">';
    h += '<span class="filter-label">Sort</span>';
    h += '<select id="mobile-sort-col" class="mobile-sort-select">';
    sortOptions.forEach(function (s) {
      h += '<option value="' + s[0] + '"' + (sortCol === s[0] ? ' selected' : '') + '>' + s[1] + '</option>';
    });
    h += '</select>';
    h += '<button type="button" id="mobile-sort-dir" class="mobile-sort-dir-btn" title="Toggle sort direction">'
       + (sortDir === 1 ? '\u25B2' : '\u25BC') + '</button>';
    h += '</div>';
    h += '</div>';
    return h;
  }

  // ── Pagination HTML ─────────────────────────────────────

  function buildPaginationHTML() {
    var tp = totalPages();
    if (tp <= 1) return '';
    var h = '<div class="portal-pagination">';
    h += '<button class="portal-page-btn" data-page="prev"' + (currentPage <= 1 ? ' disabled' : '') + '>\u00ab Prev</button>';
    h += '<span class="portal-page-info">Page ' + currentPage + ' of ' + tp + '</span>';
    h += '<button class="portal-page-btn" data-page="next"' + (currentPage >= tp ? ' disabled' : '') + '>Next \u00bb</button>';
    h += '</div>';
    return h;
  }

  function renderCell(p, col) {
    var v = p[col.key];
    var attr = ' data-label="' + col.label + '"';

    if (col.type === 'status') {
      var sc = v === 'hired' ? 'sh-st-hired' : 'sh-st-new';
      return '<td' + attr + '><select class="status-select ' + sc + '" data-field="outreach_status">'
        + opt('new', 'New', v) + opt('contacted', 'Contacted', v)
        + opt('phone', 'Phone Interviewed', v) + opt('in_person', 'In-Person', v)
        + opt('hired', 'Hired', v)
        + '</select></td>';
    }
    if (col.type === 'hire') {
      var hv = (v === 'pending' || !v) ? '' : v;
      var hc = hv === 'yes' ? 'sh-st-hire-yes' : hv === 'no' ? 'sh-st-hire-no' : hv === 'maybe' ? 'sh-st-hire-maybe' : '';
      return '<td' + attr + '><select class="hire-select ' + hc + '" data-field="hire_status">'
        + opt('', '-', hv) + opt('yes', 'Yes', hv)
        + opt('maybe', 'Maybe', hv) + opt('no', 'No', hv)
        + '</select></td>';
    }
    if (col.type === 'notes') {
      return '<td class="notes-cell"' + attr + '>'
        + '<textarea class="notes-inline" data-field="employer_notes" rows="1"'
        + ' placeholder="...">' + esc(v || '') + '</textarea></td>';
    }
    if (col.type === 'price') {
      return '<td class="cell-price"' + attr + '>$' + (v ? Number(v).toFixed(0) : '0') + '</td>';
    }
    if (col.type === 'date') {
      return '<td class="cell-date"' + attr + '>' + esc(formatRelativeDate(v)) + '</td>';
    }
    if (col.type === 'name_link') {
      var handle = p.candidate_handle || '';
      var badge = p.is_unread ? '<span class="new-badge">NEW</span>' : '';
      if (handle) {
        return '<td class="cell-name"' + attr + '>' + badge + '<a href="/products/' + esc(handle) + '" style="color:#1565C0;font-weight:600;text-decoration:none;">' + esc(v || 'Unknown') + '</a></td>';
      }
      return '<td class="cell-name"' + attr + '>' + badge + esc(v || 'Unknown') + '</td>';
    }
    if (col.key === 'candidate_name') {
      return '<td class="cell-name"' + attr + '>' + esc(v || 'Unknown') + '</td>';
    }
    if (col.key === 'candidate_metro') {
      return '<td class="cell-area"' + attr + '>' + esc(v || '') + '</td>';
    }
    if (col.key === 'candidate_phone' && v) {
      return '<td' + attr + '><a class="cell-link" href="tel:' + esc(v) + '">' + esc(fmtPhone(v)) + '</a></td>';
    }
    if (col.key === 'candidate_email' && v) {
      return '<td' + attr + '><a class="cell-link" href="mailto:' + esc(v) + '">' + esc(v) + '</a></td>';
    }
    return '<td' + attr + '>' + esc(v || '') + '</td>';
  }

  // ── Events ───────────────────────────────────────────────

  function attachHandlers() {
    // Sort headers
    document.querySelectorAll('.portal-table th[data-sort]')
      .forEach(function (el) {
        el.addEventListener('click', function () {
          setSort(el.getAttribute('data-sort'));
        });
      });

    // Mark-as-viewed: clicking the candidate name link navigates away to
    // the product page; use `keepalive` so the POST still completes.
    document.querySelectorAll('.portal-table .cell-name a')
      .forEach(function (el) {
        el.addEventListener('click', function () {
          var row = el.closest('tr');
          if (row) markViewedIfUnread(row.getAttribute('data-id'), { keepalive: true });
        });
      });

    // Mark-as-viewed: focusing the notes textarea counts as engagement.
    document.querySelectorAll('.portal-table .notes-inline')
      .forEach(function (el) {
        el.addEventListener('focus', function () {
          var row = el.closest('tr');
          if (row) markViewedIfUnread(row.getAttribute('data-id'));
        });
      });

    // Status selects — map to booleans and save
    document.querySelectorAll('.portal-table .status-select')
      .forEach(function (el) {
        el.addEventListener('change', function () {
          var row = el.closest('tr');
          var id = row.getAttribute('data-id');
          markViewedIfUnread(id);
          var val = el.value;
          var bools = statusToBools(val);
          // If "Hired" selected, also set hire_status
          if (val === 'hired') bools.hire_status = 'yes';
          updateTracking(id, bools, function () {
            var p = findPurchase(id);
            if (p) {
              p.contacted = bools.contacted;
              p.phone_interview = bools.phone_interview;
              p.in_person_interview = bools.in_person_interview;
              p.outreach_status = val;
              if (val === 'hired') p.hire_status = 'yes';
            }
            // Update select color class
            el.className = 'status-select ' + (val === 'hired' ? 'sh-st-hired' : 'sh-st-new');
            // Also update hire select if visible
            var hireEl = row.querySelector('.hire-select');
            if (hireEl && val === 'hired') {
              hireEl.value = 'yes';
              hireEl.className = 'hire-select sh-st-hire-yes';
            }
            updateSummary();
            showToast('Saved');
          });
        });
      });

    // Hire selects — update class + re-render to move between sections
    document.querySelectorAll('.portal-table .hire-select')
      .forEach(function (el) {
        el.addEventListener('change', function () {
          var row = el.closest('tr');
          var id = row.getAttribute('data-id');
          markViewedIfUnread(id);
          var val = el.value;
          var sendVal = val || 'pending';
          // Update color immediately
          var hc = val === 'yes' ? 'sh-st-hire-yes' : val === 'no' ? 'sh-st-hire-no' : val === 'maybe' ? 'sh-st-hire-maybe' : '';
          el.className = 'hire-select ' + hc;
          updateTracking(id, { hire_status: sendVal }, function () {
            var p = findPurchase(id);
            if (p) {
              p.hire_status = sendVal;
            }
            render();
            if (isArchive && val !== 'no') {
              showToast('Moved back to My Candidates');
            } else if (!isArchive && val === 'no') {
              showToast('Moved to Archive');
            } else {
              showToast('Updated');
            }
          });
        });
      });

    // Notes — save on blur
    document.querySelectorAll('.portal-table .notes-inline')
      .forEach(function (el) {
        var orig = el.value;
        el.addEventListener('blur', function () {
          if (el.value !== orig) {
            var row = el.closest('tr');
            var id = row.getAttribute('data-id');
            updateTracking(id, { employer_notes: el.value }, function() {
              var p = findPurchase(id);
              if (p) p.employer_notes = el.value;
              showToast('Saved');
            });
            orig = el.value;
          }
        });
      });

  }

  // ── Filter & Pagination Events ──────────────────────────

  function attachFilterHandlers() {
    // Status pills
    document.querySelectorAll('[data-filter="status"] .filter-pill').forEach(function (btn) {
      btn.addEventListener('click', function () {
        filterStatus = btn.getAttribute('data-status');
        currentPage = 1;
        render();
      });
    });
    // Hire pills
    document.querySelectorAll('[data-filter="hire"] .filter-pill').forEach(function (btn) {
      btn.addEventListener('click', function () {
        filterHire = btn.getAttribute('data-hire');
        currentPage = 1;
        render();
      });
    });
    // Text search (debounced)
    var searchInput = document.getElementById('portal-search');
    if (searchInput) {
      var timer;
      searchInput.addEventListener('input', function () {
        clearTimeout(timer);
        var pos = searchInput.selectionStart;
        timer = setTimeout(function () {
          filterText = searchInput.value;
          currentPage = 1;
          render();
          var si = document.getElementById('portal-search');
          if (si) { si.focus(); si.setSelectionRange(pos, pos); }
        }, 200);
      });
    }
    // Pagination
    document.querySelectorAll('.portal-page-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var dir = btn.getAttribute('data-page');
        if (dir === 'prev' && currentPage > 1) currentPage--;
        if (dir === 'next' && currentPage < totalPages()) currentPage++;
        render();
        var list = document.getElementById('candidates-list');
        if (list) list.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    // Clear filters link (zero results state)
    var clearLink = document.getElementById('clear-filters');
    if (clearLink) {
      clearLink.addEventListener('click', function (e) {
        e.preventDefault();
        filterStatus = 'all';
        filterHire = isArchive ? 'no' : '__no_no';
        filterText = '';
        currentPage = 1;
        render();
      });
    }
    // Time filter pills for summary metrics
    document.querySelectorAll('[data-filter="time"] .filter-pill').forEach(function (btn) {
      btn.addEventListener('click', function () {
        timeFilter = btn.getAttribute('data-time');
        document.querySelectorAll('[data-filter="time"] .filter-pill').forEach(function (b) {
          b.classList.toggle('active', b.getAttribute('data-time') === timeFilter);
        });
        render();
      });
    });
    // Mobile sort dropdown
    var mobileSortCol = document.getElementById('mobile-sort-col');
    if (mobileSortCol) {
      mobileSortCol.addEventListener('change', function () {
        sortCol = mobileSortCol.value;
        sortDir = sortCol === 'purchased_at' ? -1 : 1;
        currentPage = 1;
        render();
      });
    }
    var mobileSortDir = document.getElementById('mobile-sort-dir');
    if (mobileSortDir) {
      mobileSortDir.addEventListener('click', function () {
        sortDir = sortDir * -1;
        render();
      });
    }
    // Summary card clicks
    document.querySelectorAll('.summary-card--clickable').forEach(function (card) {
      card.addEventListener('click', function () {
        var t = card.getAttribute('data-filter-status');
        filterHire = isArchive ? 'no' : '__no_no';
        filterStatus = 'all';
        if (t === 'contacted') filterStatus = 'contacted';
        else if (t === 'interviewed') filterStatus = 'interviewed';
        else if (t === 'hired') filterStatus = 'hired';
        else if (t === 'passed') { filterStatus = 'all'; filterHire = 'no'; }
        currentPage = 1;
        render();
      });
    });
  }

  function highlightActiveSummaryCard() {
    document.querySelectorAll('.summary-card--clickable').forEach(function (card) {
      var t = card.getAttribute('data-filter-status');
      var on = false;
      if (t === 'all' && filterStatus === 'all' && filterHire === (isArchive ? 'no' : '__no_no')) on = true;
      else if (t === 'contacted' && filterStatus === 'contacted') on = true;
      else if (t === 'interviewed' && filterStatus === 'interviewed') on = true;
      else if (t === 'hired' && filterStatus === 'hired') on = true;
      else if (t === 'passed' && filterHire === 'no') on = true;
      card.classList.toggle('active-filter', on);
    });
  }

  // ── Helpers ──────────────────────────────────────────────

  function findPurchase(id) {
    for (var i = 0; i < purchases.length; i++) {
      if (String(purchases[i].id) === String(id)) return purchases[i];
    }
    return null;
  }

  var MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  function formatRelativeDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var now = new Date();
    var diffMs = now.getTime() - d.getTime();
    if (diffMs < 0) diffMs = 0;
    var minutes = Math.floor(diffMs / 60000);
    var hours = Math.floor(diffMs / 3600000);
    var days = Math.floor(diffMs / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return minutes + 'm ago';
    if (hours < 24) return hours + 'h ago';
    if (days === 1) return 'Yesterday';
    if (days < 7) return days + ' days ago';
    if (d.getFullYear() === now.getFullYear()) {
      return MONTH_ABBR[d.getMonth()] + ' ' + d.getDate();
    }
    return MONTH_ABBR[d.getMonth()] + ' ' + d.getFullYear();
  }

  // Fire-and-forget mark-as-viewed. Sends only when the row is still
  // unread; clears the visual state in-place so callers don't have to
  // trigger a full re-render. `keepalive: true` lets the request finish
  // when the click navigates away to the candidate product page.
  function markViewedIfUnread(purchaseId, opts) {
    var p = findPurchase(purchaseId);
    if (!p || !p.is_unread) return;
    p.is_unread = false;
    p.first_viewed_at = new Date().toISOString();

    var fetchOpts = {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ purchase_id: Number(purchaseId), customer_email: email })
    };
    if (opts && opts.keepalive) fetchOpts.keepalive = true;
    fetch(base + '/viewed', fetchOpts).catch(function () {});

    var row = document.querySelector('.portal-table tr[data-id="' + purchaseId + '"]');
    if (row) {
      row.classList.remove('row-unread');
      var badge = row.querySelector('.new-badge');
      if (badge && badge.parentNode) badge.parentNode.removeChild(badge);
    }
  }

  function updateSummary() {
    var tp = filterByTime(purchases);
    if (isArchive) {
      tp = tp.filter(function (p) { return p.hire_status === 'no'; });
    }
    var nC = 0, nI = 0, nH = 0;
    tp.forEach(function (p) {
      if (p.contacted) nC++;
      if (p.phone_interview || p.in_person_interview) nI++;
      if (p.hire_status === 'yes') nH++;
    });
    setText('total-purchased', tp.length);
    setText('total-contacted', nC);
    setText('total-interviewed', nI);
    setText('total-hired', nH);
  }

  function esc(s) {
    if (!s) return '';
    var d = document.createElement('div');
    d.appendChild(document.createTextNode(s));
    return d.innerHTML;
  }

  function fmtPhone(p) {
    if (!p) return '';
    var d = p.replace(/\D/g, '');
    if (d.length === 11 && d[0] === '1') d = d.substring(1);
    if (d.length === 10) return '(' + d.substring(0,3) + ') ' + d.substring(3,6) + '-' + d.substring(6);
    return p;
  }

  function opt(val, text, cur) {
    return '<option value="' + val + '"' + (cur === val ? ' selected' : '') + '>' + text + '</option>';
  }

  function setText(id, val) {
    var el = document.getElementById(id);
    if (el) el.textContent = val;
  }

  // ── Toast Notification ──────────────────────────────────

  function showToast(msg) { window.shToast(msg, 'success'); }

  // ── CSV Export ─────────────────────────────────────────

  function exportCSV() {
    var headers = ['Name', 'Area', 'Phone', 'Email', 'Status', 'Hire', 'Notes', 'Date'];
    var rows = [headers.join(',')];
    var statusLabels = { 'new': 'New', contacted: 'Contacted', phone: 'Phone Interviewed', in_person: 'In-Person Interview' };
    var exportList = isArchive
      ? purchases.filter(function(p) { return p.hire_status === 'no'; })
      : purchases;
    exportList.forEach(function(p) {
      var d = p.purchased_at ? new Date(p.purchased_at) : null;
      var ds = d ? (d.getMonth()+1)+'/'+d.getDate()+'/'+d.getFullYear() : '';
      rows.push([
        csvEsc(p.candidate_name || ''),
        csvEsc(p.candidate_metro || ''),
        csvEsc(p.candidate_phone || ''),
        csvEsc(p.candidate_email || ''),
        statusLabels[p.outreach_status] || 'New',
        p.hire_status || '',
        csvEsc(p.employer_notes || ''),
        ds
      ].join(','));
    });
    var blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = isArchive ? 'skimhire-archived-candidates.csv' : 'skimhire-candidates.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function csvEsc(s) {
    if (!s) return '';
    if (s.indexOf(',') !== -1 || s.indexOf('"') !== -1 || s.indexOf('\n') !== -1) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  }

  // ── Init ─────────────────────────────────────────────────

  fetchPurchases(function() {
    render();
  });

  // Re-fetch on EVERY pageshow (initial load, full reload, BFCache restore).
  // Unconditional by design — refund / tracking changes don't set any flag, and SSE on
  // other pages can suppress BFCache. A single idempotent fetch per pageshow is the
  // robust path. Fixes B7-001.
  window.addEventListener('pageshow', function() {
    fetchPurchases(function() { render(); });
  });
})();

